/**
 * Question Option Shuffling — Content Domain
 *
 * Pure shuffling utilities for question options.
 * Supports deterministic shuffling via seeded RNG.
 *
 * Created in R38 (DDD Fase 7 — questionLoader god-file split).
 */

import { QuestionCore, Question, QuestionType } from '../types';
import { getQuestionTypeConfig } from './questionTypeRegistry';
import { createRng } from '../../utils/deterministicRng';

/**
 * Fisher-Yates shuffle with pluggable RNG.
 */
export function fisherYatesShuffle<T>(array: T[], randomFn: () => number): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(randomFn() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Hash question ID to numeric seed (for option shuffling).
 */
export function hashQuestionId(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash << 5) - hash + id.charCodeAt(i) | 0;
  }
  return hash >>> 0;
}

/**
 * Shuffle question options and update correctAnswer accordingly.
 *
 * @param question - Question to shuffle
 * @param seed - Optional seed for deterministic shuffling
 */
export function shuffleQuestionOptions<T extends QuestionCore>(question: T, seed?: number): T {
  const type = question.questionType;
  const config = getQuestionTypeConfig(type);
  if (!config.shouldShuffleOptions) return question;

  // Guard: skip shuffling if options is missing or empty
  if (!question.options || question.options.length === 0) return question;

  // Create RNG (deterministic if seed provided, random otherwise)
  const rng = seed !== undefined ? createRng(seed + hashQuestionId(question.id)) : null;
  const randomFn = () => rng ? rng.random() : Math.random();
  const indices = fisherYatesShuffle(question.options.map((_, i) => i), randomFn);
  const shuffledOptions = indices.map(i => question.options[i]);
  const newCorrectAnswer = indices.indexOf(question.correctAnswer);
  return {
    ...question,
    options: shuffledOptions,
    correctAnswer: newCorrectAnswer
  } as T;
}

/**
 * Shuffle options for a full Question (with explanations).
 *
 * @param seed - Optional seed for deterministic shuffling used by governance/QA diagnostics
 */
export function shuffleQuestionWithExplanations(question: Question, seed?: number): Question {
  const type = question.questionType;
  const config = getQuestionTypeConfig(type);
  if (!config.shouldShuffleOptions) return question;

  // Guard: skip shuffling if options is missing or empty
  if (!question.options || question.options.length === 0) return question;
  const rng = seed !== undefined ? createRng(seed + hashQuestionId(question.id)) : null;
  const randomFn = () => rng ? rng.random() : Math.random();
  const indices = fisherYatesShuffle(question.options.map((_, i) => i), randomFn);
  const shuffledOptions = indices.map(i => question.options[i]);
  const newCorrectAnswer = indices.indexOf(question.correctAnswer);
  let remappedDistractorExplanations: Record<string, string> | undefined;
  const currentDistractorExplanations = question.feedback?.distractorExplanations;
  if (currentDistractorExplanations) {
    remappedDistractorExplanations = {};
    indices.forEach((originalIndex, newIndex) => {
      const originalExplanation = currentDistractorExplanations[originalIndex.toString()];
      if (originalExplanation) {
        remappedDistractorExplanations![newIndex.toString()] = originalExplanation;
      }
    });
  }
  return {
    ...question,
    options: shuffledOptions,
    correctAnswer: newCorrectAnswer,
    feedback: {
      ...question.feedback,
      distractorExplanations: remappedDistractorExplanations
    }
  };
}